import FlashOnIcon from '@mui/icons-material/FlashOn'; // fallback for ElectricBoltIcon
import OpacityIcon from '@mui/icons-material/Opacity';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SwitchRightIcon from '@mui/icons-material/SwitchRight';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  SvgIcon,
} from '@mui/material';
import { calculatePercentage } from 'helpers/helpers.js';
import { useState } from 'react';
import { brandColors } from 'types/colors';
import { TimeInMS } from 'types/time';
// import { ReactComponent as KWHSAVE } from 'assets/images/icons/KWHSAVE.svg';
// import { ReactComponent as KWHWASTE } from 'assets/images/icons/KWHWASTE.svg';
import vemImage from 'assets/images/vem2.png';
import BoltIcon from '@mui/icons-material/Bolt';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import { ReactComponent as Co2Icon } from 'assets/images/icons/CO2.svg';
import leafIcon from 'assets/images/icons/leaf.png';
import ForestIcon from '@mui/icons-material/Forest';
import { useSelector } from 'react-redux';
import PingPongText from 'components/PingPongText/PingPongText';

function SimpleProgressBar({ value = 0, height = 10, color = brandColors.BLUE, background = '#e0e0e0', borderRadius = 999 }) {
  // Clamp between 0 and 100
  const percent = Math.min(100, Math.max(0, value));

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: background,
        borderRadius,
        overflow: 'hidden',
        height,
      }}>
      <Box
        sx={{
          width: `${percent}%`,
          backgroundColor: color,
          height: '100%',
          borderRadius,
          transition: 'width 0.4s ease',
        }}
      />
    </Box>
  );
}

const getSumOfHoursForSeletedTimeRange = timeRange => {
  // Handle millisecond values
  if (typeof timeRange === 'number') {
    if (timeRange === 86400000) {
      // todaye
      const nowMs = Date.now(); // current time in ms since epoch

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0); // set to today at 00:00
      const todayStartMs = todayStart.getTime();

      const elapsedMs = nowMs - todayStartMs;
      return Math.round((elapsedMs / (1000 * 60 * 60)) * 10) / 10;
    }
    // Convert milliseconds to hours
    return timeRange / (1000 * 60 * 60);
  }

  // Handle date range string format: yyyy-mm-dd_yyyy-mm-dd
  if (typeof timeRange === 'string' && timeRange.includes('_')) {
    const [startDateStr, endDateStr] = timeRange.split('_');

    // Parse dates
    const startDate = new Date(startDateStr + 'T00:00:00');
    const endDate = new Date(endDateStr + 'T23:59:59.999');

    // Calculate difference in milliseconds
    const diffMs = endDate.getTime() - startDate.getTime();

    // Convert to hours and round to nearest integer
    return Math.round(diffMs / (1000 * 60 * 60));
  }

  throw new Error('Invalid timeRange format. Expected number (ms) or string format "yyyy-mm-dd_yyyy-mm-dd"');
};
// unitType, coolingType, heatingType, cavStartTime, cavEndTime

// import { isValidElement } from React;

/**
 * UI‑only summary for the red box area on the Controller page.
 *
 * Pass data via props — this component contains NO data‑fetching logic.
 *
 * Props:
 *  - hvacOnCount: number
 *  - coolWaterOpenCount: number
 *  - kwhTotal: number
 *  - loading?: boolean (optional skeleton state)
 */
export default function KwhInfo({
  hvacOnCount = 0,
  coolWaterOpenCount = 0,
  kwhTotalWaste = 0,
  kwhWastePotential = 0,
  avgCoolWaterValveWhenActive = 0,
  loading = false,
  timeRangeInUnix,
  totalCavNumOfHours,
  unitType,
  coolingType,
  heatingType,
}) {
  const [runtimeView, setRuntimeView] = useState('hvac');
  const [usedMeteringView, setUsedMeteringView] = useState('used');
  const isLastHour = timeRangeInUnix === TimeInMS.Hour;
  const clampedHvacOnCount = isLastHour ? Math.min(hvacOnCount, 12) : hvacOnCount;
  const clampedCoolWaterOpenCount = isLastHour ? Math.min(coolWaterOpenCount, 12) : coolWaterOpenCount;
  const controllerModel = useSelector(state => state.controllers.currentMeasurements?.controller_model);
  const isRelayModel = ['RELAY_2CH', 'RELAY_2CH_NC', 'RELAY_4CH', 'RELAY_4CH_NC'].includes(controllerModel);

  const totalHoursNumber = getSumOfHoursForSeletedTimeRange(timeRangeInUnix);
  // const totalCavHoursNumber = calculateCavHours(timeRangeInUnix, NumOfCavHoursPerDay);
  const onHoursPercentage = calculatePercentage(clampedHvacOnCount, totalCavNumOfHours);
  const waterOnHoursPercentage = calculatePercentage(clampedCoolWaterOpenCount, totalCavNumOfHours);

  // console.log('onHoursPercentage:', onHoursPercentage);
  // console.log('waterOnHoursPercentage:', waterOnHoursPercentage);

  // console.log(`hvacOnCount: ${hvacOnCount}`);

  const hvacValuePrimary = (
    <>
      {isLastHour ? (
        <Box sx={{ display: 'flex', flexDirection: 'flex-start', alignItems: 'center' }}>
          <Typography variant="h3" fontWeight={600} lineHeight={1.1} color="#000">
            {`${clampedHvacOnCount * 5} minutes / `}
          </Typography>
          <Typography variant="h3" fontWeight={600} lineHeight={1.1} color="text.secondary" ml={1}>
            {` 1 h`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'flex-start', alignItems: 'center' }}>
          <Typography variant="h3" fontWeight={600} lineHeight={1.1} sx={{ color: '#000' }}>
            {`${fmtNumber((clampedHvacOnCount * 5) / 60, 1)} h / `}
          </Typography>
          <Typography variant="h3" fontWeight={600} lineHeight={1.1} color="text.secondary" sx={{ ml: 0.5 }}>
            {` ${totalCavNumOfHours} h`}
          </Typography>
        </Box>
      )}
    </>
  );

  const waterValuePrimary = (
    <>
      {isLastHour ? (
        <Box sx={{ display: 'flex', flexDirection: 'flex-start', alignItems: 'center' }}>
          <Typography variant="h3" fontWeight={600} lineHeight={1.1} color="#000">
            {`${clampedCoolWaterOpenCount * 5} minutes /  `}
          </Typography>
          <Typography variant="h3" fontWeight={600} lineHeight={1.1} color="text.secondary" ml={1}>
            {`${timeRangeInUnix / (1000 * 60 * 60)} h`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'flex-start', alignItems: 'center' }}>
          <Typography variant="h3" fontWeight={600} lineHeight={1.1} color="#000">
            {`${fmtNumber(clampedCoolWaterOpenCount / 12, 1)} h / `}
          </Typography>
          <Typography variant="h3" fontWeight={600} lineHeight={1.1} sx={{ ml: 0.5 }} color="text.secondary">
            {`${totalCavNumOfHours} h`}
          </Typography>
        </Box>
      )}
    </>
  );

  const runtimeViews = {
    hvac: {
      title: totalHoursNumber === 1 ? 'HVAC unit ON time / Last hour' : 'HVAC unit ON time / CAV hours',
      toggleLabel: 'Cool water',
      valuePrimary: hvacValuePrimary,
      valueSecondary: `${fmtNumber(onHoursPercentage, 0)}% of CAV hours from total CAV hours in selected time range, ${totalCavNumOfHours} ${totalHoursNumber === 1 ? `hour` : `hours`} (total)`,
      icon: (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#55555530',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <PowerSettingsNewIcon sx={{ color: '#555555', fontSize: 22, width: 26, height: 26 }} />
        </Box>
      ),
      progress: onHoursPercentage,
      progressLabel: `${fmtNumber(onHoursPercentage, 0)}% runtime`,
    },
    ...(!isRelayModel
      ? {
          water: {
            title: totalHoursNumber === 1 ? 'HVAC unit ON time / Last hour' : 'Cool water ON time / CAV hours',
            toggleLabel: 'HVAC unit power',
            valuePrimary: waterValuePrimary,
            valueSecondary: `${fmtNumber(waterOnHoursPercentage, 0)}% of CAV hours from total CAV hours in selected time range, ${totalCavNumOfHours} ${totalHoursNumber === 1 ? `hour` : `hours`} (total)`,
            icon: (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: `${brandColors.BLUE}30`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <OpacityIcon sx={{ color: brandColors.BLUE, fontSize: 22, width: 26, height: 26 }} />
              </Box>
            ),
            progress: waterOnHoursPercentage,
            progressLabel: `${fmtNumber(waterOnHoursPercentage, 1)}% open`,
            avgValve: avgCoolWaterValveWhenActive,
          },
        }
      : {}),
  };

  const usedMeteringViews = {
    used: {
      title: 'Virtual Energy Metering - Used',
      toggleLabel: 'Energy used',
      icon: (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#ff840030',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <BoltIcon sx={{ color: '#ff8400', width: 30, height: 30 }} />
        </Box>
      ),
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }} className="ENERGY-CONSUMPTION-CONTAINER-PARENT">
          {loading ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton height={44} width="50%" />
              <Skeleton height={44} width="50%" />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', height: 'fit-content' }} className="ENERGY-CONSUMPTION-CONTAINER">
              <Box
                sx={{
                  width: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  border: `1px solid #aeaeae2f`,
                  paddingX: '8px',
                  paddingY: '14px',
                  justifyContent: 'space-evenly',
                }}>
                <Box sx={{ display: 'flex', width: '100%', minWidth: 0, marginBottom: '1rem' }}>
                  <ElectricBoltIcon sx={{ color: '#ff8400', width: 20, mr: 0.4, height: 20, flexShrink: 0 }} />
                  <Typography
                    className="title"
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                    fontSize={13}
                    sx={{ flex: 1, minWidth: 0 }}>
                    <PingPongText text="ENERGY CONSUMPTION" />
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 0.5,
                    pl: 1,
                  }}>
                  <Typography variant="h4" fontWeight={600} lineHeight={1.1} color={'#000'} fontSize="2rem">
                    {fmtNumber(kwhTotalWaste, 1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    kWh
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  width: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  border: `1px solid #aeaeae2f`,
                  backgroundColor: '#aeaeae0f',
                  paddingX: '8px',
                  paddingY: '14px',
                  justifyContent: 'space-evenly',
                }}>
                <Box sx={{ display: 'flex', width: '100%', minWidth: 0, marginBottom: '1rem' }}>
                  <SvgIcon
                    sx={{
                      color: '#434343',
                      scale: '1.6',
                      marginRight: '.4rem',
                      flexShrink: 0,
                    }}>
                    <Co2Icon />
                  </SvgIcon>
                  <Typography
                    className="title"
                    noWrap
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                    fontSize={13}
                    sx={{ flex: 1, minWidth: 0 }}>
                    <PingPongText text="CO₂ EMISSIONS" />
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 0.5,
                    pl: 1,
                  }}>
                  <Typography variant="h4" fontWeight={600} lineHeight={1.1} color={'#000'} fontSize="2rem">
                    {fmtNumber(kwhTotalWaste * 0.459, 1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    KG
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Box sx={{ padding: '12px' }}>
            <Typography variant="body2" color="text.secondary" fontSize=".9rem">
              Sum of kWh and CO₂ in the selected time range
            </Typography>
          </Box>
        </Box>
      ),
    },
    placeholder: {
      title: 'Virtual Energy Metering - Baseline (CAV)',
      toggleLabel: 'Baseline (CAV)',
      icon: (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#ff000030',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <BoltIcon sx={{ color: '#ff0000', width: 30, height: 30 }} />
        </Box>
      ),
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }} className="ENERGY-CONSUMPTION-CONTAINER-PARENT">
          {loading ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton height={44} width="50%" />
              <Skeleton height={44} width="50%" />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', height: 'fit-content' }} className="ENERGY-CONSUMPTION-CONTAINER">
              <Box
                sx={{
                  width: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  border: `1px solid #aeaeae2f`,
                  paddingX: '8px',
                  paddingY: '14px',
                  justifyContent: 'space-evenly',
                }}>
                <Box sx={{ display: 'flex', width: '100%', minWidth: 0, marginBottom: '1rem' }}>
                  <ElectricBoltIcon sx={{ color: '#ff0000', width: 20, mr: 0.4, height: 20, flexShrink: 0 }} />
                  <Typography
                    className="title"
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                    fontSize={13}
                    sx={{ flex: 1, minWidth: 0 }}>
                    <PingPongText text="ENERGY CONSUMPTION" />
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 0.5,
                    pl: 1,
                  }}>
                  <Typography variant="h4" fontWeight={600} lineHeight={1.1} color={'#000'} fontSize="2rem">
                    {fmtNumber(kwhWastePotential, 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    kWh
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  width: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  border: `1px solid #aeaeae2f`,
                  backgroundColor: '#aeaeae0f',
                  paddingX: '8px',
                  paddingY: '14px',
                  justifyContent: 'space-evenly',
                }}>
                <Box sx={{ display: 'flex', width: '100%', minWidth: 0, marginBottom: '1rem' }}>
                  <SvgIcon
                    sx={{
                      color: '#434343',
                      scale: '1.6',
                      marginRight: '.4rem',
                      flexShrink: 0,
                    }}>
                    <Co2Icon />
                  </SvgIcon>
                  <Typography
                    className="title"
                    noWrap
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                    fontSize={13}
                    sx={{ flex: 1, minWidth: 0 }}>
                    <PingPongText text="CO₂ EMISSIONS" />
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 0.5,
                    pl: 1,
                  }}>
                  <Typography variant="h4" fontWeight={600} lineHeight={1.1} color={'#000'} fontSize="2rem">
                    {fmtNumber(kwhWastePotential * 0.459, 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    KG
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Box sx={{ padding: '12px' }}>
            <Typography variant="body2" color="text.secondary" fontSize=".9rem">
              Estimated energy and CO2 impact if smart optimizations were disabled
            </Typography>
          </Box>
        </Box>
      ),
    },
  };

  return (
    <Box sx={{ width: '100%', pt: { xs: 1.5, sm: 2 } }}>
      <Grid
        className="vem-container"
        container
        alignItems="stretch"
        sx={{
          flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' },
          width: '100%',
        }}>
        <Grid
          item
          id="solvem"
          xs={12}
          lg={2}
          sx={{
            display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'flex' },
            justifyContent: { xs: 'center', sm: 'center', md: 'center', lg: 'center' },
          }}>
          <Card
            item
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#ffffff00',
              boxShadow: 'none',
            }}>
            <CardContent
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 0,
              }}>
              <Box
                className="solvem-image"
                component="img"
                src={vemImage}
                alt="VEM"
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.2))', // subtle shadow on the image itself
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid
          id="data-container"
          item
          xs={12}
          lg={10}
          sx={{
            width: '100%',
          }}>
          <Grid
            container
            columnSpacing={{ xs: 0, sm: 0, md: 2 }}
            rowSpacing={{ xs: 2, sm: 2 }}
            sx={{
              width: '100%',
              flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' },
            }}>
            <Grid item xs={12} lg={4}>
              <ToggleStatCard
                loading={loading}
                views={runtimeViews}
                activeKey={runtimeView}
                onToggle={nextViewKey => setRuntimeView(nextViewKey)}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <ToggleStatCard
                loading={loading}
                views={usedMeteringViews}
                activeKey={usedMeteringView}
                onToggle={nextViewKey => setUsedMeteringView(nextViewKey)}
                cardSx={{ borderRadius: 3 }}
                contentSx={{ px: 0, py: 0, '&:last-child': { pb: 0 } }}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 3, padding: '0px' }} id="kwh-saved">
                <CardHeader
                  sx={{
                    '& .MuiCardHeader-avatar': {
                      marginRight: '8px',
                    },
                    p: 1,
                  }}
                  avatar={
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: '#09914430',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <EnergySavingsLeafIcon sx={{ color: '#099144', width: 26, height: 26 }} />
                    </Box>
                  }
                  title={
                    <Typography sx={{ fontWeight: 500, fontSize: '.9rem' }} variant="subtitle2">
                      Virtual Energy Metering - Avoided
                    </Typography>
                  }
                />
                <CardContent
                  sx={{
                    px: 0,
                    py: 0,
                    '&:last-child': {
                      pb: 0,
                    },
                  }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {loading ? (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Skeleton height={44} width="50%" />
                        <Skeleton height={44} width="50%" />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', height: 'fit-content' }} className="ENERGY-SAVED-CONTAINER">
                        <Box
                          sx={{
                            width: '50%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            border: `1px solid #aeaeae2f`,
                            paddingX: '8px',
                            paddingY: '14px',
                            justifyContent: 'space-evenly',
                          }}>
                          <Box sx={{ display: 'flex', width: '100%', minWidth: 0, marginBottom: '1rem' }}>
                            <Box
                              className="leaf-icon"
                              role="img"
                              aria-label="Leaf"
                              sx={{
                                width: 22,
                                height: 22,
                                bgcolor: '#099144',
                                mr: 0.7,
                                flexShrink: 0,
                                WebkitMaskImage: `url(${leafIcon})`,
                                maskImage: `url(${leafIcon})`,
                                WebkitMaskRepeat: 'no-repeat',
                                maskRepeat: 'no-repeat',
                                WebkitMaskSize: 'contain',
                                maskSize: 'contain',
                                WebkitMaskPosition: 'center',
                                maskPosition: 'center',
                              }}
                            />
                            <Typography
                              className="title"
                              noWrap
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              fontSize={13}
                              sx={{ flex: 1, minWidth: 0 }}>
                              <PingPongText text="ENERGY SAVED" />
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-end',
                              gap: 0.5,
                              pl: 1,
                            }}>
                            <Typography variant="h4" fontWeight={600} lineHeight={1.1} color={'#000'} fontSize="2rem">
                              {fmtNumber(kwhWastePotential - kwhTotalWaste, 1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              kWh
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            width: '50%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            border: `1px solid #aeaeae2f`,
                            backgroundColor: '#aeaeae0f',
                            paddingX: '8px',
                            paddingY: '14px',
                            justifyContent: 'space-evenly',
                          }}>
                          <Box sx={{ display: 'flex', width: '100%', minWidth: 0, marginBottom: '1rem' }}>
                            <ForestIcon sx={{ color: '#099144', width: 20, height: 20, mr: 0.7, flexShrink: 0 }} />
                            <Typography
                              className="title"
                              noWrap
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              fontSize={13}
                              sx={{ flex: 1, minWidth: 0 }}>
                              <PingPongText text="CO₂ SAVED" />
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-end',
                              gap: 0.5,
                              pl: 1,
                            }}>
                            <Typography variant="h4" fontWeight={600} lineHeight={1.1} color={'#000'} fontSize="2rem">
                              {fmtNumber((kwhWastePotential - kwhTotalWaste) * 0.459, 1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              KG
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ padding: '12px' }}>
                      <Typography variant="body2" color="text.secondary" fontSize=".9rem">
                        Energy (kWh) and CO₂ emissions savings achieved through Sol-In DCV.
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

function ToggleStatCard({ views, activeKey, onToggle, loading = false, cardSx = {}, contentSx = {} }) {
  const viewKeys = Object.keys(views || {});
  const safeActiveKey = viewKeys.includes(activeKey) ? activeKey : viewKeys[0];
  const activeIndex = Math.max(0, viewKeys.indexOf(safeActiveKey));
  const nextIndex = viewKeys.length > 0 ? (activeIndex + 1) % viewKeys.length : 0;
  const nextKey = viewKeys[nextIndex];

  if (!safeActiveKey) return null;

  const activeView = views[safeActiveKey];
  const nextView = views[nextKey] || activeView;
  const slideWidth = viewKeys.length * 100;
  const paneWidth = viewKeys.length > 0 ? `${100 / viewKeys.length}%` : '100%';
  const translate = `translateX(-${activeIndex * (100 / viewKeys.length)}%)`;

  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3, ...cardSx }}>
      <CardHeader
        sx={{
          alignItems: 'center',
          '& .MuiCardHeader-avatar': {
            marginRight: '5px',
          },
          '& .MuiCardHeader-content': {
            minWidth: 0,
            overflow: 'hidden',
          },
          '& .MuiCardHeader-action': {
            alignSelf: 'center',
            flexShrink: 0,
            margin: 0,
          },
          p: 1,
        }}
        avatar={activeView.icon}
        title={<PingPongText text={activeView.title} sx={{ fontSize: '.9rem', fontWeight: 500 }} />}
        action={
          viewKeys.length > 1 ? (
            <Tooltip title={`Switch to ${nextView.toggleLabel}`} arrow>
              <IconButton
                size="medium"
                sx={{ width: 32, height: 32 }}
                onClick={() => onToggle?.(nextKey)}
                aria-label={`Switch to ${nextView.toggleLabel}`}>
                <SwitchRightIcon />
              </IconButton>
            </Tooltip>
          ) : null
        }
      />
      <CardContent sx={contentSx}>
        <Box sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              width: `${slideWidth}%`,
              transform: translate,
              transition: 'transform 0.35s ease',
            }}>
            {viewKeys.map((key, index) => (
              <Box
                key={key}
                sx={{
                  width: paneWidth,
                  // px: 1,
                  // ...(index === 0 ? { pl: 0, pr: 2 } : {}),
                  ...(index === viewKeys.length - 1 ? { pl: 0, pr: 0 } : {}),
                }}>
                {views[key]?.content || <RuntimeCardPane view={views[key]} loading={loading} />}
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function RuntimeCardPane({ view, loading = false }) {
  return (
    <Stack spacing={1.25}>
      {loading ? (
        <Skeleton height={44} width="60%" />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Typography variant="h4" fontWeight={700} lineHeight={1.1} sx={{ color: '#000' }}>
            {view.valuePrimary}
          </Typography>

          {view.avgValve != null && (
            <Typography variant="h6" fontWeight={400} lineHeight={1.1} color="text.secondary">
              Avg valve: {String(view.avgValve)}%
            </Typography>
          )}
        </Box>
      )}
      {loading ? (
        <Skeleton width={160} height={24} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {String(view.valueSecondary)}
        </Typography>
      )}

      <Tooltip title={view.progressLabel || ''} arrow>
        <Box>{loading ? <Skeleton height={10} /> : <SimpleProgressBar label="" value={view.progress} />}</Box>
      </Tooltip>
    </Stack>
  );
}

function fmtNumber(n, precision = 1) {
  const num = typeof n === 'number' ? n : Number(n ?? 0);
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: precision,
  }).format(num);
}
